import {
  Breakpoint,
  ButtonSize,
  ButtonTheme,
  ChooseAnswersFieldsGroupAnswerType,
  ChooseAnswersFieldsGroupField,
  DualOptionField,
  FontWeight,
  FormButton,
  FormButtonTheme,
  Markdown,
  QuizAnswer,
  QuizAnswerSize,
  QuizzAnswerIconType,
  Typography,
  TypographyElement,
  TypographyVariant,
  UseState,
} from '@elearning-platform/ui';
import React, { RefObject, useMemo, useRef, useState } from 'react';
import { TransProps, useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';

import { isSameArray } from '../../../../utils';
import { ChoiceQuizType } from '../../enums';
import { ChoiceQuizQuestion, ChoiceQuizQuestionOption } from '../../interfaces';

import { initialQuizState, quizOffset } from './choiceQuiz.consts';
import {
  ButtonContainer,
  Header,
  QuestionsContainer,
  ResultContentContainer,
  ResultImage,
  ResultImageContainer,
  ResultQuestionsContainer,
  ResultWrapper,
  StyledButton,
  StyledTypography,
  TryAgainContainer,
  Wrapper,
} from './choiceQuiz.styled';
import { ChoiceQuizFeedback, ChoiceQuizProps, ChoiceQuizState } from './choiceQuiz.types';
import { scrollToRefWithOffset } from './choiceQuiz.utils';

export const ChoiceQuiz: React.FC<ChoiceQuizProps> = (props: ChoiceQuizProps): JSX.Element => {
  const { questions, quizImages, type, header, subheader, baseUrl, callComplete, layoutSettings }: ChoiceQuizProps = props;
  const { t }: TransProps<never> = useTranslation();
  const isSmallDesktop: boolean = useMediaQuery({
    query: Breakpoint.SmallDesktop,
  });
  const [result, setResult]: UseState<ChoiceQuizState> = useState<ChoiceQuizState>(initialQuizState);
  const quizRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
  const isMultipleChoice: boolean = useMemo((): boolean => type === ChoiceQuizType.MultipleChoice, [type]);
  const [attemptsCount, setAttemptsCount]: UseState<number> = useState<number>(0);

  const getLabelsFromId: (question: ChoiceQuizQuestion, ids: string | string[]) => string = (
    question: ChoiceQuizQuestion, ids: string | string[]
  ): string => {
    const idArray: string[] = Array.isArray(ids) ? ids : [ids];

    const labels: string[] = idArray.flatMap((id: string): string[] => {
      const option: ChoiceQuizQuestionOption | undefined = question.options
        .find((option: ChoiceQuizQuestionOption): boolean => option.id.toString() === id);

      return option?.label
        ? [option.label]
        : [];
    });

    return labels.join('\n');
  };

  const handleSubmit: () => void = (): void => {
    const feedback: ChoiceQuizFeedback[] = questions.map((question: ChoiceQuizQuestion): ChoiceQuizFeedback => {
      const answer: string | string[] = result.answers[question.id];
      const correct: string | string[] = question.correctAnswer;

      const baseFeedback: ChoiceQuizFeedback = {
        question: question.question,
        isCorrect: false,
        correctAnswers: getLabelsFromId(question, correct),
        additionalInfo: question.answerAdditionalInfo,
      };

      switch (type) {
        case ChoiceQuizType.SingleChoice:
          return {
            ...baseFeedback,
            isCorrect: answer === correct,
          };
        case ChoiceQuizType.MultipleChoice:
          if (Array.isArray(answer) && Array.isArray(correct)) {
            return {
              ...baseFeedback,
              isCorrect: isSameArray(answer, correct),
              correctAnswers: getLabelsFromId(question, correct),
            };
          }

          return baseFeedback;
        default:
          return baseFeedback;
      }
    });
    const isEveryQuestionCorrect: boolean = feedback.every((question: ChoiceQuizFeedback): boolean => question.isCorrect);
    const isQuizPassed: boolean = isEveryQuestionCorrect || attemptsCount === 1;

    setResult((prev: ChoiceQuizState): ChoiceQuizState => ({
      ...prev,
      isSubmitted: true,
      feedback,
      isEveryQuestionCorrect,
      isQuizPassed,
    }));
    setAttemptsCount(attemptsCount + 1);
    scrollToRefWithOffset(quizRef, quizOffset);

    if (isQuizPassed) {
      callComplete();
    }
  };

  const handleAnswersUpdate: (questionId: string, selectedAnswers: string[]) => void = (
    questionId: string,
    selectedAnswers: string[],
  ): void => {
    if (selectedAnswers.length) {
      setResult((prev: ChoiceQuizState): ChoiceQuizState => ({
        ...prev,
        answers: {
          ...prev.answers,
          [questionId]: isMultipleChoice ? selectedAnswers : selectedAnswers[0],
        },
      }));
    } else {
      setResult((prev: ChoiceQuizState): ChoiceQuizState => {
        const updatedAnswers: Record<string, string | string[]> = { ...prev.answers };
        delete updatedAnswers[questionId];

        return {
          ...prev,
          answers: updatedAnswers,
        };
      });
    }
  };

  return (
    <Wrapper ref={quizRef}>
      {!result.isSubmitted ? (
        <>
          {!!header && (
            <Header>
              {!!subheader && (
                <StyledTypography fontWeight={FontWeight.Bold} variant={TypographyVariant.Subheader}>
                  {subheader}
                </StyledTypography>
              )}
              <Typography fontWeight={FontWeight.Bold} tag={TypographyElement.div} variant={TypographyVariant.DisplayXS}>
                <Markdown text={header} />
              </Typography>
            </Header>
          )}
          <QuestionsContainer>
            {questions.map((question: ChoiceQuizQuestion): JSX.Element => (
              <DualOptionField
                content={question.question}
                fields={question.options.map((option: ChoiceQuizQuestionOption): ChooseAnswersFieldsGroupField => {
                  const selected: string | string[] = result.answers[question.id];
                  const isSelected: boolean = isMultipleChoice
                    ? Array.isArray(selected) && selected.includes(option.id)
                    : selected === option.id;

                  return {
                    ...option,
                    isSelected,
                  };
                })}
                header={question.header}
                isVertical={layoutSettings?.isQuestionVertical}
                key={question.id}
                onChange={(selectedAnswers: string[]): void => handleAnswersUpdate(question.id, selectedAnswers)}
                type={!isMultipleChoice
                  ? ChooseAnswersFieldsGroupAnswerType.SingleChoice
                  : ChooseAnswersFieldsGroupAnswerType.MultipleChoice
                }
              />
            ))}
          </QuestionsContainer>
        </>
      ) : (
        <ResultWrapper>
          <ResultContentContainer>
            <Typography fontWeight={FontWeight.Bold} variant={isSmallDesktop ? TypographyVariant.DisplaySM : TypographyVariant.TextXL}>
              {result.isQuizPassed
                ? result.isEveryQuestionCorrect
                  ? t('competence.app.scrollLesson.componentsV2.choiceQuiz.successHeadline')
                  : t('competence.app.scrollLesson.componentsV2.choiceQuiz.failsLimitHeadline')
                : t('competence.app.scrollLesson.componentsV2.choiceQuiz.failHeadline')
              }
            </Typography>
            {!!result.isSubmitted && (
              result.isQuizPassed ? (
                <ResultQuestionsContainer>
                  {result.feedback?.map((question: ChoiceQuizFeedback): JSX.Element => (
                    <QuizAnswer
                      additionalInfo={question.additionalInfo}
                      answer={question.correctAnswers}
                      content={question.question}
                      iconType={question.isCorrect ? QuizzAnswerIconType.Correct : QuizzAnswerIconType.Incorrect}
                      key={question.question}
                      size={question.additionalInfo ? QuizAnswerSize.Medium : QuizAnswerSize.Small}
                    />
                  ))}
                </ResultQuestionsContainer>
              ) : (
                <TryAgainContainer>
                  <Typography variant={isSmallDesktop ? TypographyVariant.DisplayXS : TypographyVariant.TextXL}>
                    {t('competence.app.scrollLesson.componentsV2.choiceQuiz.failSupportingText')}
                  </Typography>
                  {!isSmallDesktop && (
                    <ResultImageContainer>
                      <ResultImage src={`${baseUrl}${result.isQuizPassed ? quizImages.successImage : quizImages.failImage}`} />
                    </ResultImageContainer>
                  )}
                  <StyledButton
                    fullWidth={!isSmallDesktop}
                    label={t('competence.app.scrollLesson.componentsV2.choiceQuiz.tryAgain')}
                    onClick={(): void => setResult(initialQuizState)}
                    size={ButtonSize.Large}
                    variant={ButtonTheme.PrimaryBrand}
                  />
                </TryAgainContainer>
              )
            )}
          </ResultContentContainer>
          {!!isSmallDesktop && (
            <ResultImageContainer>
              <ResultImage
                src={`${baseUrl}${(result.isQuizPassed && result.isEveryQuestionCorrect) ? quizImages.successImage : quizImages.failImage}`}
              />
            </ResultImageContainer>
          )}
        </ResultWrapper>
      )}
      {!result.isSubmitted && (
        <ButtonContainer>
          <FormButton
            disabled={Object.keys(result.answers).length !== questions.length}
            fullWidth={!isSmallDesktop}
            label={t('competence.app.scrollLesson.componentsV2.choiceQuiz.confirm')}
            onClick={handleSubmit}
            variant={FormButtonTheme.SecondaryBrand}
          />
        </ButtonContainer>
      )}
    </Wrapper>
  );
};
